/// <amd-module name="Analytics/Boards/CumulativeFlowDiagramQueries/BoardColumnsQuery" />
import { AnalyticsCacheableQueryBase } from "Analytics/Common/AnalyticsCacheableQueryBase";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";

interface ColumnRevision {
    /** The backlog level name the column is associated with. */
    BoardCategoryReferenceName: string;
    ColumnId: string;
    ColumnName: string;
    ColumnOrder: number;

    /**
     * The max (latest) RevisedDate of the BoardLocation.
     * If the date equals the end of time (9999-01-01T00:00:00Z) then the column is NOT deleted.
     * If the date doesn't equal the end of time, then either this is an old revision (due to rename, reorder, or otherwise)
     * or the column is deleted.
     *
     * Given in UTC.
     */
    LatestRevisedDate: string;
}

export interface Column {
    ColumnId: string;
    ColumnName: string;
    ColumnOrder: number;
    RevisedDate: string;
    IsDeleted: boolean;
}

export interface BoardsColumnQueryResult {
    boardColumns: Column[];
    boardCategoryReferenceName: string;
}

/**
 * Retrieves the latest state of columns (active and deleted) of a given board.
 * @param project ID or name of a project.
 * @param teamId ID of a board within the given project.
 */
export class BoardColumnsQuery extends AnalyticsCacheableQueryBase<BoardsColumnQueryResult> {
    /**
     * The date used by analytics and WIT to describe the property associated with the date as "current".
     * For example, a WorkItemRevision with a RevisedDate set to "end of time" means that it is the latest revision.
     */
    private static readonly endOfTime: Date = new Date(Date.UTC(9999, 0, 1));

    private static generateQueryOptions(project: string, boardId: string): ODataQueryOptions {
        return {
            entityType: "BoardLocations",
            oDataVersion: latestODataVersion,
            project: project,
            $apply: `filter(BoardId eq ${boardId})/groupby((BoardCategoryReferenceName, ColumnId, ColumnName, ColumnOrder), aggregate(RevisedDate with max as LatestRevisedDate))`
        };
    }

    constructor(command: string, project: string, boardId: string) {
        super(command, BoardColumnsQuery.generateQueryOptions(project, boardId));
    }

    public getQueryName(): string {
        return "BoardColumnsQuery";
    }

    protected interpretQueryResults({ value: columns }: { value: ColumnRevision[] }): { boardColumns: Column[]; boardCategoryReferenceName: string } {
        // The query data contains column revision information and may contain multiple entries for the same column ID due to renames/reordering.
        // We only want the latest revision for each column ID.
        return this.filterColumnRevisionsToLatest(columns);
    }

    /**
     * Returns an array containing the latest revision for each distinct column ID in the given array of column revisions.
     * @param revisions - The column revisions to filter
     */
    private filterColumnRevisionsToLatest(revisions: ColumnRevision[]): { boardColumns: Column[]; boardCategoryReferenceName: string } {
        const latestColumnRevisionsMap: { [key: string]: ColumnRevision } = {};
        for (const revision of revisions) {
            const latestKnownRevision = latestColumnRevisionsMap[revision.ColumnId];
            if (latestKnownRevision != null) {
                const columnRevisedDate = new Date(revision.LatestRevisedDate);
                const latestKnownRevisedDate = new Date(latestKnownRevision.LatestRevisedDate);

                // Save the latest revision
                if (columnRevisedDate.getTime() > latestKnownRevisedDate.getTime()) {
                    latestColumnRevisionsMap[revision.ColumnId] = revision;
                }
            } else {
                latestColumnRevisionsMap[revision.ColumnId] = revision;
            }
        }

        // Map to array
        const latestColumnRevisions: Column[] = [];
        for (const key in latestColumnRevisionsMap) {
            const revision = latestColumnRevisionsMap[key];
            const revisedDate = new Date(revision.LatestRevisedDate);
            const isDeleted = revisedDate.getTime() !== BoardColumnsQuery.endOfTime.getTime();

            latestColumnRevisions.push({
                ColumnId: revision.ColumnId,
                ColumnName: revision.ColumnName,
                ColumnOrder: revision.ColumnOrder,
                RevisedDate: revision.LatestRevisedDate,
                IsDeleted: isDeleted
            });
        }

        return {
            boardColumns: latestColumnRevisions,
            boardCategoryReferenceName: revisions[0].BoardCategoryReferenceName
        };
    }
}
